import { useEffect, useState } from 'react';
import { isProductionUpdateChecksEnabled } from '@/lib/appUpdate';

const POLL_MS = 60 * 1000;

type VersionPayload = { buildId?: string };

export function useAppUpdateAvailable(): boolean {
  const embeddedBuildId = import.meta.env.VITE_BUILD_ID ?? '';
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!isProductionUpdateChecksEnabled() || !embeddedBuildId) return;

    let cancelled = false;

    async function check() {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { Pragma: 'no-cache' }
        });
        if (!res.ok) return;
        const data = (await res.json()) as VersionPayload;
        if (!cancelled && data.buildId && data.buildId !== embeddedBuildId) {
          setUpdateAvailable(true);
        }
      } catch {
        /* network blip — retry on next poll */
      }
    }

    void check();
    const timer = window.setInterval(() => void check(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onFocus = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [embeddedBuildId]);

  return updateAvailable;
}
